// src/components/fix-actions/action-log-tab.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Search, Download, Loader2, AlertTriangle, ChevronDown, ChevronRight,
  Sparkles, X, CheckCircle2, Clock, Send, FileText, ArrowRight,
  Target, TrendingUp, Zap, ExternalLink, Activity,
} from "lucide-react";
import { UnderlineTabs, UnderlineTabsPanel } from "@/app/components/shared/underline-tabs";
import type { FixAction } from "../../services/fix-actions/fix-actions-service";
import {
  fixActionsService,
  type DeployLogEntry,
  type DeployStatus,
  type ActionEventStatusItem,
} from "../../services/fix-actions/fix-actions-service";
import { Button } from "../kit";

type EventType = string;
type EventStatus = "Open" | "In-Progress" | "Closed" | string;

type ActionLogEvent = {
  id: string;
  eventType: EventType;
  status: EventStatus;
  timestamp: string;
  actionTitle: string;
  actionDescription: string;
  affectedObjects: Array<{ id: string; type: string; name: string }>;
  additionalContext?: string;
  actionDetails?: {
    priority?: string;
    effort?: string;
    impactLevel?: string;
    impactScore?: number;
    category?: string;
    strategicTheme?: string;
    observation?: string;
    strategicGoal?: string;
    artifactCount?: number;
  };
  fixActionId?: number;
};

const DEPLOY_TABLE_MIN_W = 720;
const EVENT_TABLE_MIN_W = 780;

function sourceToEventType(sourceModule?: string): EventType {
  const s = String(sourceModule || "").toLowerCase();
  if (s === "antenna") return "Action from Monitoring";
  if (s === "cognition") return "Action from Cognition";
  return "Suggested Action";
}

function deriveEventStatus(
  action: FixAction,
  completedActionIds: Set<number>,
  eventStatusMap: Map<number, ActionEventStatusItem>,
  generatedAssetCount?: number
): EventStatus {
  const backendStatus = eventStatusMap.get(action.id);
  if (backendStatus) {
    if (backendStatus.eventStatus === "Closed") return "Closed";
    if (backendStatus.eventStatus === "In-Progress") return "In-Progress";
    if (backendStatus.eventStatus === "Open" && (generatedAssetCount ?? 0) > 0) return "In-Progress";
  }
  if (completedActionIds.has(action.id)) return "Closed";
  const s = (action.status || "").toLowerCase();
  if (s === "completed") return "Closed";
  if (s === "in-progress" || s === "in_progress") return "In-Progress";
  if ((generatedAssetCount ?? 0) > 0) return "In-Progress";
  return "Open";
}

function buildEventsFromEntries(
  entries: FixAction[],
  completedActionIds: Set<number>,
  eventStatusMap: Map<number, ActionEventStatusItem>,
  generatedAssetCountMap: Map<number, number>
): ActionLogEvent[] {
  return (entries || []).map((a) => ({
    id: String((a as any).sourceId || a.id),
    timestamp: a.createdAt || new Date().toISOString(),
    eventType: sourceToEventType((a as any).sourceModule),
    status: deriveEventStatus(a, completedActionIds, eventStatusMap, generatedAssetCountMap.get(a.id)),
    actionTitle: a.title || "Untitled Action",
    actionDescription: a.description || "",
    affectedObjects: [{ id: String(a.id), type: "Suggested Action", name: a.title }],
    additionalContext: (a as any).stageId ? `Stage: ${(a as any).stageId}` : undefined,
    actionDetails: {
      priority: a.priority,
      effort: a.effort,
      impactLevel: a.impactLevel,
      impactScore: a.impactScore,
      category: a.category,
      strategicTheme: a.strategicTheme,
      observation: a.observation,
      strategicGoal: a.strategicGoal,
      artifactCount: a.artifacts?.length ?? 0,
    },
    fixActionId: a.id,
  }));
}

function formatTs(ts: string) {
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const relative =
    diffMins < 60 ? `${diffMins}m ago`
    : diffHours < 24 ? `${diffHours}h ago`
    : diffDays < 7 ? `${diffDays}d ago`
    : date.toLocaleDateString();
  const absolute = date.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  return { relative, absolute };
}

function exportToCSV(events: ActionLogEvent[]): string {
  const headers = ["id", "timestamp", "eventType", "status", "actionTitle", "priority", "impact"];
  const rows = events.map((e) => [
    e.id, e.timestamp, e.eventType, e.status, e.actionTitle,
    e.actionDetails?.priority || "", e.actionDetails?.impactLevel || "",
  ]);
  const escape = (v: any) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
}

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function getStatusColors(status: EventStatus) {
  if (status === "Closed") return { bg: "var(--alpha-teal-10)", text: "var(--alpha-teal)", border: "var(--alpha-teal-20)" };
  if (status === "In-Progress") return { bg: "var(--signal-blue-10)", text: "var(--signal-blue)", border: "var(--signal-blue-30)" };
  return { bg: "var(--muted)", text: "var(--muted-foreground)", border: "var(--border)" };
}

function getPriorityColor(priority?: string) {
  const p = (priority || "").toUpperCase();
  if (p === "HIGH") return "var(--infrared-coral)";
  if (p === "MEDIUM") return "var(--signal-blue)";
  return "var(--muted-foreground)";
}

function getPriorityBadgeStyle(priority?: string) {
  const p = (priority || "").toUpperCase();
  if (p === "HIGH") return { bg: "var(--infrared-coral-10)", text: "var(--infrared-coral)", border: "var(--infrared-coral-20)" };
  if (p === "MEDIUM") return { bg: "var(--signal-blue-10)", text: "var(--signal-blue)", border: "var(--signal-blue-30)" };
  return { bg: "var(--muted)", text: "var(--muted-foreground)", border: "var(--border)" };
}

const DEPLOY_STATUS_OPTIONS: DeployStatus[] = ["Draft", "In Review", "Ready", "Published"];

function deployStatusColors(status: DeployStatus) {
  if (status === "Published") return { bg: "var(--alpha-teal-10)", text: "var(--alpha-teal)", border: "var(--alpha-teal-20)" };
  if (status === "Ready") return { bg: "var(--success-bg)", text: "var(--success-text)", border: "var(--success-border)" };
  if (status === "In Review") return { bg: "var(--warning-bg)", text: "var(--warning-text)", border: "var(--warning-border)" };
  return { bg: "var(--muted)", text: "var(--muted-foreground)", border: "var(--border)" };
}

/**
 * Inline pill chip used inside a tab label to show a status count.
 * Renders nothing when count is zero so the tab trigger doesn't collect
 * empty "0 open" noise.
 */
function CountChip({ count, tone }: { count: number; tone: "muted" | "info" | "success" }) {
  if (!count) return null;
  const palette =
    tone === "success"
      ? { bg: "var(--alpha-teal-10)", fg: "var(--alpha-teal)" }
      : tone === "info"
      ? { bg: "var(--signal-blue-10)", fg: "var(--signal-blue)" }
      : { bg: "var(--muted)", fg: "var(--muted-foreground)" };
  const suffix = tone === "success" ? "closed" : tone === "info" ? "in progress" : "open";
  return (
    <span
      className="px-1.5 py-0.5 rounded-full"
      style={{
        backgroundColor: palette.bg,
        color: palette.fg,
        fontSize: "10px",
        fontWeight: "var(--font-weight-bold)",
        letterSpacing: "0.02em",
      }}
    >
      {count} {suffix}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Deploy status selector
// ─────────────────────────────────────────────────────────────
function DeployStatusSelector({
  entryId, current, onUpdate,
}: {
  entryId: number;
  current: DeployStatus;
  onUpdate: (id: number, status: DeployStatus) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const colors = deployStatusColors(current);

  const select = async (status: DeployStatus) => {
    setOpen(false);
    if (status === current) return;
    setSaving(true);
    await onUpdate(entryId, status);
    setSaving(false);
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        disabled={saving}
        style={{
          display: "inline-flex", alignItems: "center", gap: "4px",
          padding: "4px 10px", borderRadius: "var(--radius)",
          backgroundColor: colors.bg, color: colors.text,
          border: `1px solid ${colors.border}`,
          fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)",
          cursor: "pointer",
        }}
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : current}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div style={{
            position: "absolute", top: "100%", left: 0, zIndex: 50, marginTop: "4px",
            backgroundColor: "var(--popover)", border: "1px solid var(--border)",
            borderRadius: "var(--radius)", boxShadow: "var(--elevation-md)", minWidth: "140px",
          }}>
            {DEPLOY_STATUS_OPTIONS.filter((s) => s !== "Published").map((s) => {
              const c = deployStatusColors(s);
              return (
                <button
                  key={s}
                  onClick={(e) => { e.stopPropagation(); select(s); }}
                  style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    width: "100%", textAlign: "left", padding: "8px 12px",
                    fontSize: "var(--text-sm)",
                    color: s === current ? c.text : "var(--foreground)",
                    backgroundColor: s === current ? c.bg : "transparent",
                    border: "none", cursor: "pointer",
                  }}
                >
                  {s === current && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: c.text }} />}
                  {s}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Deploy log panel
// ─────────────────────────────────────────────────────────────
function DeployLogPanel({
  brandId, actionEntries, onNavigateToAction, onArtifactStatusChange,
}: {
  brandId: string;
  actionEntries: FixAction[];
  onNavigateToAction?: (actionTitle: string) => void;
  onArtifactStatusChange?: (artifactId: string, status: DeployStatus) => void;
}) {
  const [entries, setEntries] = useState<DeployLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<DeployStatus | "all">("all");
  const [search, setSearch] = useState("");

  const actionTitleMap = useMemo(() => {
    const m = new Map<number, string>();
    actionEntries.forEach((a) => { if (a.id && a.title) m.set(a.id, a.title); });
    return m;
  }, [actionEntries]);

  const fetchLog = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fixActionsService.getDeployLog({ brandId });
      setEntries(res.items || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load deploy log");
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  useEffect(() => {
    const handler = () => fetchLog();
    window.addEventListener("deploy_log_refresh", handler);
    return () => window.removeEventListener("deploy_log_refresh", handler);
  }, [fetchLog]);

  const handleStatusUpdate = async (id: number, status: DeployStatus) => {
    const entry = entries.find((e) => e.id === id);
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));

    try {
      await fixActionsService.patchDeployLogStatus({ id, status, brandId });

      if (entry?.artifactId && onArtifactStatusChange) {
        onArtifactStatusChange(entry.artifactId, status);
      } else if (status === "Published" && entry) {
        window.dispatchEvent(new CustomEvent("artifact_published", {
          detail: {
            artifactId: entry.artifactId,
            actionId: entry.actionId,
            actionTitle: actionTitleMap.get(entry.actionId) ?? "",
          },
        }));
        window.dispatchEvent(new Event("deploy_log_refresh"));
      }
    } catch (e) {
      console.error("Deploy status update failed", e);
      if (entry) {
        setEntries((prev) => prev.map((row) => row.id === id ? { ...row, status: entry.status } : row));
      }
    }
  };

  const filtered = useMemo(() => {
    let items = entries;
    if (filterStatus !== "all") items = items.filter((e) => e.status === filterStatus);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((e) =>
        e.artifactName.toLowerCase().includes(q) ||
        (actionTitleMap.get(e.actionId) || "").toLowerCase().includes(q)
      );
    }
    return items;
  }, [entries, filterStatus, search, actionTitleMap]);

  if (loading)
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--signal-blue)" }} />
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center py-24 px-4">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--infrared-coral)" }} />
          <p style={{ color: "var(--muted-foreground)", marginBottom: "12px" }}>{error}</p>
          <Button variant="secondary" onClick={fetchLog}>Retry</Button>
        </div>
      </div>
    );

  return (
    <div>
      <div
        className="px-4 md:px-8 py-3 border-b"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--alpha-teal-5)" }}
      >
        <p style={{ fontSize: "var(--text-sm)", color: "var(--alpha-teal)" }}>
          💡 An asset reads <strong>Published</strong> once it is published from Brand Publisher (or the publication is recorded by hand); that closes the Action Event. Click any row to view the action in Suggested Actions.
        </p>
      </div>

      <div
        className="px-4 md:px-8 py-3 md:py-4 border-b flex flex-wrap items-center gap-3"
        style={{ borderColor: "var(--border)" }}
      >
        <div className="relative w-full sm:w-auto sm:flex-1 sm:max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted-foreground)" }} />
          <input
            type="text"
            placeholder="Search actions or assets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-md"
            style={{
              backgroundColor: "var(--background)",
              borderColor: "var(--border)",
              fontSize: "var(--text-sm)",
            }}
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          className="px-3 py-2 border rounded-md"
          style={{
            backgroundColor: "var(--background)",
            borderColor: "var(--border)",
            fontSize: "var(--text-sm)",
            color: "var(--foreground)",
          }}
        >
          <option value="all">All statuses</option>
          {DEPLOY_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="ml-auto" style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
          {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="flex items-center justify-center py-24 px-4">
          <div className="text-center max-w-md">
            <div
              className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "var(--muted)" }}
            >
              <Send className="w-8 h-8" style={{ color: "var(--muted-foreground)" }} />
            </div>
            <h3 style={{ fontSize: "var(--text-xl)", fontWeight: "var(--font-weight-semibold)", marginBottom: "12px" }}>
              {entries.length === 0 ? "No deployed assets yet" : "No matching entries"}
            </h3>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
              {entries.length === 0
                ? 'After you click "Approve & Deploy" on a generated asset, it appears here.'
                : "Try adjusting your filters."}
            </p>
          </div>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="overflow-x-auto">
          <div style={{ minWidth: DEPLOY_TABLE_MIN_W }}>
            <div
              className="px-4 md:px-8 py-2 border-b"
              style={{ borderColor: "var(--border)", backgroundColor: "var(--muted)" }}
            >
              <div
                className="grid items-center gap-4"
                style={{ gridTemplateColumns: "1fr 160px 160px 120px" }}
              >
                {["Action / Asset", "Status", "Last Updated", "Created"].map((h) => (
                  <span key={h} style={{
                    fontSize: "var(--text-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--muted-foreground)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>{h}</span>
                ))}
              </div>
            </div>

            {filtered.map((entry) => {
              const updated = formatTs(entry.updatedAt || entry.createdAt);
              const created = formatTs(entry.createdAt);
              const isPublished = entry.status === "Published";
              const actionTitle = actionTitleMap.get(entry.actionId) ?? "";
              const canNavigate = Boolean(actionTitle && onNavigateToAction);

              return (
                <div
                  key={entry.id}
                  className="px-4 md:px-8 py-3 border-b transition-colors hover:bg-muted/30"
                  style={{
                    borderColor: "var(--border)",
                    backgroundColor: isPublished ? "var(--alpha-teal-5)" : undefined,
                    cursor: canNavigate ? "pointer" : "default",
                  }}
                  onClick={() => { if (canNavigate) onNavigateToAction!(actionTitle); }}
                >
                  <div
                    className="grid items-center gap-4"
                    style={{ gridTemplateColumns: "1fr 160px 160px 120px" }}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      <div className="mt-0.5 flex-shrink-0">
                        {isPublished ? <CheckCircle2 className="w-4 h-4" style={{ color: "var(--alpha-teal)" }} />
                        : entry.status === "Ready" ? <CheckCircle2 className="w-4 h-4" style={{ color: "var(--success-text)" }} />
                        : entry.status === "In Review" ? <Clock className="w-4 h-4" style={{ color: "var(--warning-text)" }} />
                        : <FileText className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />}
                      </div>

                      <div className="min-w-0 flex-1">
                        {actionTitle ? (
                          <div style={{
                            fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)",
                            color: "var(--foreground)", overflow: "hidden", textOverflow: "ellipsis",
                            whiteSpace: "nowrap", marginBottom: "2px",
                          }} title={actionTitle}>{actionTitle}</div>
                        ) : null}

                        <div style={{
                          fontSize: actionTitle ? "var(--text-xs)" : "var(--text-sm)",
                          fontWeight: actionTitle ? "var(--font-weight-normal)" : "var(--font-weight-medium)",
                          color: actionTitle ? "var(--muted-foreground)" : "var(--foreground)",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }} title={entry.artifactName}>{entry.artifactName}</div>

                        {entry.artifactType && (
                          <div style={{ fontSize: "10px", color: "var(--muted-foreground)", marginTop: "1px" }}>
                            {entry.artifactType}
                          </div>
                        )}
                      </div>

                      {canNavigate && (
                        <div title="View in Suggested Actions">
                          <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "var(--muted-foreground)", opacity: 0.5 }} />
                        </div>
                      )}
                    </div>

                    <div onClick={(e) => e.stopPropagation()}>
                      <DeployStatusSelector entryId={entry.id} current={entry.status} onUpdate={handleStatusUpdate} />
                    </div>

                    <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }} title={updated.absolute}>
                      {updated.relative}
                    </span>
                    <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }} title={created.absolute}>
                      {created.relative}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main exported component
// ─────────────────────────────────────────────────────────────
interface ActionLogTabProps {
  entries: FixAction[];
  allActions?: FixAction[];
  loading: boolean;
  error: string | null;
  onNavigateToAction?: (actionTitle: string) => void;
  onNavigateToAsset?: (assetId: string) => void;
  brandId?: string;
  completedActionIds?: Set<number>;
  initialExpandTitle?: string | null;
  onInitialExpandConsumed?: () => void;
  onArtifactStatusChange?: (artifactId: string, status: DeployStatus) => void;
}

export function ActionLogTab({
  entries, allActions, loading, error,
  onNavigateToAction, onNavigateToAsset,
  brandId = "default",
  completedActionIds = new Set(),
  initialExpandTitle, onInitialExpandConsumed,
  onArtifactStatusChange,
}: ActionLogTabProps) {
  const [activeSubTab, setActiveSubTab] = useState<"events" | "deploy">("events");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEventType, setFilterEventType] = useState<EventType | "all">("all");
  const [filterStatus, setFilterStatus] = useState<EventStatus | "all">("all");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showExportMenu, setShowExportMenu] = useState(false);

  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const [eventStatusMapState, setEventStatusMap] = useState<Map<number, ActionEventStatusItem>>(new Map());

  const refreshEventStatuses = useCallback(async () => {
    try {
      const res = await fixActionsService.getEventStatuses({ brandId });
      const m = new Map<number, ActionEventStatusItem>();

      const titleToIngestedId = new Map<string, number>();
      entries.forEach((e) => { if (e.title) titleToIngestedId.set(e.title.trim().toLowerCase(), e.id); });

      const suggestedIdToTitle = new Map<number, string>();
      (allActions ?? entries).forEach((a) => { if (a.title) suggestedIdToTitle.set(a.id, a.title.trim().toLowerCase()); });

      (res.statuses || []).forEach((s) => {
        m.set(s.actionId, s);
        const title = suggestedIdToTitle.get(s.actionId);
        if (title) {
          const ingestedId = titleToIngestedId.get(title);
          if (ingestedId && ingestedId !== s.actionId) m.set(ingestedId, s);
        }
      });

      setEventStatusMap(m);
    } catch { /* non-fatal */ }
  }, [brandId, entries, allActions]);

  useEffect(() => { refreshEventStatuses(); }, [refreshEventStatuses]);

  useEffect(() => {
    window.addEventListener("artifact_published", refreshEventStatuses);
    window.addEventListener("event_status_updated", refreshEventStatuses);
    return () => {
      window.removeEventListener("artifact_published", refreshEventStatuses);
      window.removeEventListener("event_status_updated", refreshEventStatuses);
    };
  }, [refreshEventStatuses]);

  const [generatedAssetCountMap, setGeneratedAssetCountMap] = useState<Map<number, number>>(new Map());

  const refreshGeneratedAssetCounts = useCallback(async () => {
    try {
      const res = await fixActionsService.getDeployLog({ brandId });
      if (!res.items?.length) { setGeneratedAssetCountMap(new Map()); return; }

      const titleToIngestedId = new Map<string, number>();
      entries.forEach((e) => { if (e.title) titleToIngestedId.set(e.title.trim().toLowerCase(), e.id); });

      const suggestedIdToTitle = new Map<number, string>();
      (allActions ?? entries).forEach((a) => { if (a.title) suggestedIdToTitle.set(a.id, a.title.trim().toLowerCase()); });

      const m = new Map<number, number>();
      res.items.forEach((item) => {
        const title = suggestedIdToTitle.get(item.actionId);
        const ingestedId = title ? titleToIngestedId.get(title) : undefined;
        const key = ingestedId ?? item.actionId;
        m.set(key, (m.get(key) ?? 0) + 1);
      });

      setGeneratedAssetCountMap(m);
    } catch { /* non-fatal */ }
  }, [brandId, entries, allActions]);

  useEffect(() => { refreshGeneratedAssetCounts(); }, [refreshGeneratedAssetCounts]);

  useEffect(() => {
    const handler = () => refreshGeneratedAssetCounts();
    window.addEventListener("deploy_log_refresh", handler);
    window.addEventListener("event_status_updated", handler);
    window.addEventListener("optimization_studio_refresh", handler);
    return () => {
      window.removeEventListener("deploy_log_refresh", handler);
      window.removeEventListener("event_status_updated", handler);
      window.removeEventListener("optimization_studio_refresh", handler);
    };
  }, [refreshGeneratedAssetCounts]);

  useEffect(() => {
    const handler = () => setActiveSubTab("deploy");
    window.addEventListener("switch_to_deploy_tab", handler);
    return () => window.removeEventListener("switch_to_deploy_tab", handler);
  }, []);

  const events = useMemo(
    () => buildEventsFromEntries(entries, completedActionIds, eventStatusMapState, generatedAssetCountMap),
    [entries, completedActionIds, eventStatusMapState, generatedAssetCountMap]
  );

  useEffect(() => {
    if (!initialExpandTitle || activeSubTab !== "events") return;
    if (events.length === 0) return;

    const needle = initialExpandTitle.trim().toLowerCase();
    const match = events.find((e) => e.actionTitle.trim().toLowerCase() === needle);
    if (!match) return;

    setExpandedRows((prev) => { const next = new Set(prev); next.add(match.id); return next; });

    requestAnimationFrame(() => {
      const el = rowRefs.current.get(match.id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    onInitialExpandConsumed?.();
  }, [initialExpandTitle, events, activeSubTab, onInitialExpandConsumed]);

  const filteredEvents = useMemo(() => {
    let filtered = events;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((e) => e.actionTitle.toLowerCase().includes(q) || String(e.eventType).toLowerCase().includes(q));
    }
    if (filterEventType !== "all") filtered = filtered.filter((e) => e.eventType === filterEventType);
    if (filterStatus !== "all") filtered = filtered.filter((e) => e.status === filterStatus);
    return filtered;
  }, [events, searchQuery, filterEventType, filterStatus]);

  const hasActiveFilters = filterEventType !== "all" || filterStatus !== "all" || searchQuery !== "";
  const clearFilters = () => {
    setFilterEventType("all"); setFilterStatus("all"); setSearchQuery("");
  };

  const toggleRow = (id: string) => {
    const next = new Set(expandedRows);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedRows(next);
  };

  const handleExport = (format: "csv" | "json") => {
    const ts = new Date().toISOString().split("T")[0];
    if (format === "csv") downloadBlob(exportToCSV(filteredEvents), `action-log-${ts}.csv`, "text/csv");
    else downloadBlob(JSON.stringify(filteredEvents, null, 2), `action-log-${ts}.json`, "application/json");
    setShowExportMenu(false);
  };

  const openCount = useMemo(() => events.filter((e) => e.status === "Open").length, [events]);
  const inProgressCount = useMemo(() => events.filter((e) => e.status === "In-Progress").length, [events]);
  const closedCount = useMemo(() => events.filter((e) => e.status === "Closed").length, [events]);

  return (
    <div>
      <UnderlineTabs
        tabs={[
          {
            value: "events",
            label: "Action Events",
            labelExtra: (
              <>
                <CountChip count={openCount} tone="muted" />
                <CountChip count={inProgressCount} tone="info" />
                <CountChip count={closedCount} tone="success" />
              </>
            ),
          },
          { value: "deploy", label: "Deploy Tracking" },
        ]}
        value={activeSubTab}
        onChange={(v) => setActiveSubTab(v as "events" | "deploy")}
        size="sm"
      >
        <UnderlineTabsPanel value="deploy">
          <DeployLogPanel
            brandId={brandId}
            actionEntries={allActions ?? entries}
            onNavigateToAction={onNavigateToAction}
            onArtifactStatusChange={onArtifactStatusChange}
          />
        </UnderlineTabsPanel>

        <UnderlineTabsPanel value="events">
          {loading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--signal-blue)" }} />
            </div>
          )}
          {!loading && error && (
            <div className="flex items-center justify-center py-24 px-4">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 mb-4" style={{ color: "var(--infrared-coral)" }} />
                <p style={{ color: "var(--muted-foreground)" }}>{error}</p>
              </div>
            </div>
          )}
          {!loading && !error && events.length === 0 && (
            <div className="flex items-center justify-center py-24 px-4">
              <div className="text-center max-w-md">
                <Sparkles className="w-16 h-16 mx-auto mb-6" style={{ color: "var(--muted-foreground)" }} />
                <h3 style={{ fontSize: "var(--text-xl)", fontWeight: "var(--font-weight-semibold)", marginBottom: "12px" }}>
                  No activity yet
                </h3>
                <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                  Generate an optimization asset from <strong>Suggested Actions</strong> to see it tracked here.
                </p>
              </div>
            </div>
          )}
          {!loading && !error && events.length > 0 && (
            <>
              <div
                className="px-4 md:px-8 py-3 border-b"
                style={{ borderColor: "var(--border)", backgroundColor: "var(--spectral-violet-5)" }}
              >
                <p style={{ fontSize: "var(--text-sm)", color: "var(--spectral-violet)" }}>
                  💡 <strong>In-Progress</strong> as soon as the first asset is generated · <strong>Closed</strong> when all assets in Deploy Tracking are Published.
                </p>
              </div>

              <div
                className="px-4 md:px-8 py-3 md:py-4 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="flex items-center gap-1 flex-wrap">
                  <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginRight: "4px" }}>
                    Filter:
                  </span>
                  {[
                    { label: "All", action: clearFilters },
                    { label: "Open", action: () => setFilterStatus("Open") },
                    { label: "In Progress", action: () => setFilterStatus("In-Progress") },
                    { label: "Closed", action: () => setFilterStatus("Closed") },
                    { label: "From Monitoring", action: () => setFilterEventType("Action from Monitoring") },
                  ].map(({ label, action }) => (
                    <Button
                      variant="ghost"
                      key={label}
                      onClick={action}
                    >
                      {label}
                    </Button>
                  ))}
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      onClick={clearFilters}
                      icon={X}
                    >Clear
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative flex-1 min-w-[180px] sm:flex-none sm:w-[220px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted-foreground)" }} />
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border rounded-md"
                      style={{
                        backgroundColor: "var(--background)",
                        borderColor: "var(--border)",
                        fontSize: "var(--text-sm)",
                      }}
                    />
                  </div>
                  <div className="relative">
                    <Button
                      variant="secondary"
                      onClick={() => setShowExportMenu(!showExportMenu)}
                      icon={Download}
                    >Export
                    </Button>
                    {showExportMenu && (
                      <div className="absolute right-0 mt-2 py-2 rounded-md shadow-lg z-50" style={{
                        backgroundColor: "var(--popover)",
                        border: "1px solid var(--border)",
                        minWidth: "160px",
                        boxShadow: "var(--elevation-md)",
                      }}>
                        <button className="w-full px-4 py-2 text-left hover:bg-muted" style={{ fontSize: "var(--text-sm)" }} onClick={() => handleExport("csv")}>
                          Export as CSV
                        </button>
                        <button className="w-full px-4 py-2 text-left hover:bg-muted" style={{ fontSize: "var(--text-sm)" }} onClick={() => handleExport("json")}>
                          Export as JSON
                        </button>
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>
                    {filteredEvents.length} event{filteredEvents.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <div style={{ minWidth: EVENT_TABLE_MIN_W }}>
                  <div
                    className="px-4 md:px-8 py-2 border-b"
                    style={{ borderColor: "var(--border)", backgroundColor: "var(--muted)" }}
                  >
                    <div className="flex items-center gap-4">
                      <div style={{ width: "40px", flexShrink: 0 }} />
                      <div style={{ minWidth: "160px", flexShrink: 0 }}>
                        <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Source
                        </span>
                      </div>
                      <div style={{ minWidth: "80px", flexShrink: 0 }}>
                        <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          When
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Action
                        </span>
                      </div>
                      <div style={{ width: "120px", flexShrink: 0 }}>
                        <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Priority
                        </span>
                      </div>
                      <div style={{ width: "130px", flexShrink: 0, textAlign: "right" }}>
                        <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Status
                        </span>
                      </div>
                    </div>
                  </div>

                  {filteredEvents.map((event) => (
                    <EventRow
                      key={event.id}
                      event={event}
                      isExpanded={expandedRows.has(event.id)}
                      onToggleExpand={() => toggleRow(event.id)}
                      onNavigateToAction={onNavigateToAction}
                      onNavigateToAsset={onNavigateToAsset}
                      rowRef={(el) => { if (el) rowRefs.current.set(event.id, el); else rowRefs.current.delete(event.id); }}
                    />
                  ))}
                </div>
              </div>

              {filteredEvents.length === 0 && hasActiveFilters && (
                <div className="flex items-center justify-center py-16 px-4">
                  <div className="text-center">
                    <p style={{ color: "var(--muted-foreground)", marginBottom: "12px" }}>No events match your filters</p>
                    <Button variant="secondary" onClick={clearFilters}>Clear filters</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </UnderlineTabsPanel>
      </UnderlineTabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Event row
// ─────────────────────────────────────────────────────────────
function EventRow({
  event, isExpanded, onToggleExpand, onNavigateToAction, rowRef,
}: {
  event: ActionLogEvent;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onNavigateToAction?: (actionTitle: string) => void;
  onNavigateToAsset?: (id: string) => void;
  rowRef?: (el: HTMLDivElement | null) => void;
}) {
  const isClosed = event.status === "Closed";
  const isInProgress = event.status === "In-Progress";
  const indicatorColor = isClosed ? "var(--alpha-teal)" : isInProgress ? "var(--signal-blue)" : "var(--muted-foreground)";
  const eventColor = event.eventType.toLowerCase().includes("monitoring") ? "var(--alpha-teal)"
    : event.eventType.toLowerCase().includes("cognition") ? "var(--signal-blue)" : "var(--foreground)";
  const statusColors = getStatusColors(event.status);
  const priorityBadge = getPriorityBadgeStyle(event.actionDetails?.priority);
  const ts = formatTs(event.timestamp);

  const navigateToAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onNavigateToAction && event.actionTitle) onNavigateToAction(event.actionTitle);
  };

  return (
    <div
      ref={rowRef}
      className="border-b transition-colors"
      style={{
        borderColor: "var(--border)",
        backgroundColor: isClosed ? "var(--alpha-teal-2)" : isInProgress ? "rgba(59,130,246,0.02)" : undefined,
      }}
    >
      <div className="px-4 md:px-8 py-4 hover:bg-muted/30 transition-colors cursor-pointer" onClick={onToggleExpand}>
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-5">
            {isExpanded ? <ChevronDown className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
              : <ChevronRight className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />}
          </div>
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: indicatorColor }} />
          <div className="flex-shrink-0" style={{ minWidth: "160px" }}>
            <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)", color: eventColor }}>
              {event.eventType}
            </span>
          </div>
          <div className="flex-shrink-0" style={{ minWidth: "80px" }}>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }} title={ts.absolute}>
              {ts.relative}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div style={{
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--foreground)",
              lineHeight: "1.4", overflow: "hidden",
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            }}>{event.actionTitle}</div>
          </div>
          {event.actionDetails?.priority && (
            <div className="flex-shrink-0" style={{ width: "120px" }}>
              <span className="px-2 py-0.5 rounded" style={{
                backgroundColor: priorityBadge.bg, color: priorityBadge.text,
                border: `1px solid ${priorityBadge.border}`,
                fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)",
              }}>{event.actionDetails.priority}</span>
            </div>
          )}
          <div className="flex-shrink-0" style={{ width: "130px", textAlign: "right" }}>
            <span className="px-2 py-0.5 rounded inline-flex items-center gap-1" style={{
              backgroundColor: statusColors.bg, color: statusColors.text,
              fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)",
              border: `1px solid ${statusColors.border}`,
            }}>
              {event.status === "In-Progress" && <Activity className="w-3 h-3" />}
              {event.status === "Closed" && <CheckCircle2 className="w-3 h-3" />}
              {event.status}
            </span>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t" style={{ borderColor: "var(--border)", backgroundColor: "var(--muted)" }}>
          <div className="px-4 md:px-8 py-4 md:py-5">
            <div
              className="rounded-xl p-4 md:p-5 mb-5"
              style={{
                backgroundColor: "var(--background)",
                border: "1px solid var(--border)",
                boxShadow: "var(--elevation-sm)",
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {event.actionDetails?.strategicTheme && (
                    <span className="px-2 py-0.5 rounded" style={{
                      fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)",
                      backgroundColor: "var(--spectral-violet-10)", color: "var(--spectral-violet)",
                      border: "1px solid var(--spectral-violet-20)",
                    }}>{event.actionDetails.strategicTheme}</span>
                  )}
                  {event.actionDetails?.priority && (
                    <span className="px-2 py-0.5 rounded" style={{
                      fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)",
                      backgroundColor: priorityBadge.bg, color: priorityBadge.text,
                      border: `1px solid ${priorityBadge.border}`,
                    }}>{event.actionDetails.priority}</span>
                  )}
                  <span className="px-2 py-0.5 rounded inline-flex items-center gap-1" style={{
                    fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)",
                    backgroundColor: statusColors.bg, color: statusColors.text,
                    border: `1px solid ${statusColors.border}`,
                  }}>
                    {event.status === "In-Progress" && <Activity className="w-3 h-3" />}
                    {event.status === "Closed" && <CheckCircle2 className="w-3 h-3" />}
                    {event.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 md:gap-4 flex-wrap">
                  {event.actionDetails?.effort && (
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-3.5 h-3.5" style={{ color: "var(--muted-foreground)" }} />
                      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>Effort:</span>
                      <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)" }}>
                        {event.actionDetails.effort}
                      </span>
                    </div>
                  )}
                  {event.actionDetails?.impactLevel && (
                    <div className="flex items-center gap-1">
                      <Zap className="w-3.5 h-3.5" style={{ color: "var(--muted-foreground)" }} />
                      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>Impact:</span>
                      <span style={{
                        fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)",
                        color: getPriorityColor(event.actionDetails.impactLevel === "High" ? "HIGH" : event.actionDetails.impactLevel === "Low" ? "LOW" : "MEDIUM"),
                      }}>
                        {event.actionDetails.impactLevel}
                        {event.actionDetails.impactScore != null && (
                          <span style={{ fontWeight: "var(--font-weight-normal)", color: "var(--muted-foreground)", marginLeft: "2px" }}>
                            ({event.actionDetails.impactScore.toFixed(0)}/100)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {event.actionDetails?.artifactCount != null && (
                    <div className="flex items-center gap-1">
                      <Target className="w-3.5 h-3.5" style={{ color: "var(--muted-foreground)" }} />
                      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                        {event.actionDetails.artifactCount} asset{event.actionDetails.artifactCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <h4 style={{
                fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)",
                marginBottom: "8px", lineHeight: "1.35", color: "var(--foreground)",
                overflowWrap: "anywhere", wordBreak: "break-word",
              }}>{event.actionTitle}</h4>
              {event.actionDescription && (
                <p style={{
                  fontSize: "var(--text-sm)", color: "var(--muted-foreground)",
                  lineHeight: "1.6", marginBottom: "16px",
                  overflowWrap: "anywhere", wordBreak: "break-word",
                }}>{event.actionDescription}</p>
              )}

              {onNavigateToAction && event.actionTitle && (
                <button
                  onClick={navigateToAction}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg hover:opacity-90 transition-opacity w-full sm:w-auto justify-center"
                  style={{
                    backgroundColor: "var(--alpha-teal)", color: "white",
                    fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)",
                    border: "none", cursor: "pointer",
                  }}
                >
                  <ExternalLink className="w-4 h-4" />
                  View in Suggested Actions
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {event.actionDetails?.observation && (
              <div className="p-4 rounded-lg mb-3" style={{
                backgroundColor: "var(--spectral-violet-5)",
                border: "1px solid var(--spectral-violet-20)",
              }}>
                <div style={{
                  fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)",
                  color: "var(--spectral-violet)", marginBottom: "6px",
                  textTransform: "uppercase", letterSpacing: "0.05em",
                }}>Observation</div>
                <p style={{
                  fontSize: "var(--text-sm)", color: "var(--foreground)",
                  lineHeight: "1.5", overflowWrap: "anywhere", wordBreak: "break-word",
                }}>{event.actionDetails.observation}</p>
              </div>
            )}
            {event.actionDetails?.strategicGoal && (
              <div className="p-4 rounded-lg mb-3" style={{
                backgroundColor: "var(--alpha-teal-5)",
                border: "1px solid var(--alpha-teal-20)",
              }}>
                <div style={{
                  fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-semibold)",
                  color: "var(--alpha-teal)", marginBottom: "6px",
                  textTransform: "uppercase", letterSpacing: "0.05em",
                }}>Strategic Goal</div>
                <p style={{
                  fontSize: "var(--text-sm)", color: "var(--foreground)",
                  lineHeight: "1.5", overflowWrap: "anywhere", wordBreak: "break-word",
                }}>{event.actionDetails.strategicGoal}</p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-3 border-t flex-wrap" style={{ borderColor: "var(--border)" }}>
              <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>Logged:</span>
              <span style={{ fontSize: "var(--text-xs)", color: "var(--foreground)", fontWeight: "var(--font-weight-medium)" }}>
                {ts.absolute}
              </span>
              <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                · {event.eventType}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}